import { useState, useEffect, useRef } from 'react';

export default function Analytics() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const metrics = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Doanh thu & Pipeline',
      description: 'Theo dõi doanh thu thực tế, dự kiến và phân tích pipeline bán hàng chi tiết',
      value: '₫2.5B',
      label: 'Doanh thu tháng',
      color: 'from-[#06b6d4] to-[#00c897]'
    },
    {
      icon: 'ri-team-line',
      title: 'Hiệu suất đội ngũ',
      description: 'Đánh giá KPI cá nhân và team, xác định top performer và cơ hội cải thiện',
      value: '94%',
      label: 'Đạt mục tiêu',
      color: 'from-[#2a4b7c] to-[#3d5a80]'
    },
    {
      icon: 'ri-bar-chart-grouped-line',
      title: 'Tiến độ dự án',
      description: 'Giám sát tiến độ, nguồn lực và rủi ro của tất cả dự án đang triển khai',
      value: '87%',
      label: 'Hoàn thành',
      color: 'from-[#2ecc71] to-[#00c897]'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Tỷ lệ chuyển đổi',
      description: 'Phân tích conversion rate từ lead đến customer và hành vi khách hàng',
      value: '32%',
      label: 'Lead to Deal',
      color: 'from-[#06b6d4] to-[#2a4b7c]'
    }
  ];

  const reportFeatures = [
    'Dashboard tùy chỉnh theo vai trò',
    'Báo cáo tự động theo lịch',
    'Export đa định dạng (PDF, Excel, CSV)',
    'Chia sẻ báo cáo an toàn',
    'Drill-down chi tiết từng số liệu',
    'Mobile app xem báo cáo mọi lúc'
  ];

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#06b6d4]/10 text-[#06b6d4] text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            <i className="ri-dashboard-line"></i>
            <span>PHÂN TÍCH & BÁO CÁO</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Phân tích & báo cáo theo thời gian thực
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Cung cấp insights sâu sắc và dữ liệu chính xác để ra quyết định kinh doanh thông minh
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12 md:mb-16">
          {metrics.map((metric, index) => (
            <div key={index} className={`bg-gradient-to-br from-gray-50 to-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100 hover:border-[#06b6d4]/30 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`} style={{ transitionDelay: `${200 + index * 100}ms` }}>
              <div className="flex items-start justify-between mb-4 sm:mb-6">
                <div className={`w-10 h-10 sm:w-12 sm:h-14 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${metric.color}`}>
                  <i className={`${metric.icon} text-lg sm:text-xl md:text-2xl text-white`}></i>
                </div>
                <div className="text-right">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">{metric.value}</div>
                  <div className="text-xs sm:text-sm text-gray-500">{metric.label}</div>
                </div>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{metric.title}</h3>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{metric.description}</p>
            </div>
          ))}
        </div>

        {/* Report Features */}
        <div className={`bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-white transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-center">
            <div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6">
                Báo cáo mạnh mẽ & linh hoạt
              </h3>
              <p className="text-white/80 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
                Tạo và tùy chỉnh báo cáo theo nhu cầu cụ thể của doanh nghiệp, 
                từ tổng quan chiến lược đến chi tiết vận hành
              </p>
              <ul className="space-y-3 sm:space-y-4">
                {reportFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-[#00c897] flex-shrink-0">
                      <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                    </div>
                    <span className="text-sm sm:text-base text-white/90">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mt-6 md:mt-0">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20analytics%20dashboard%20showing%20real-time%20business%20metrics%20charts%20graphs%20KPIs%20revenue%20pipeline%20team%20performance%20and%20conversion%20rates%2C%20clean%20professional%20interface%20with%20data%20visualization%20bar%20charts%20line%20graphs%20and%20pie%20charts%2C%20cyan%20and%20teal%20color%20scheme%20with%20white%20background%2C%203D%20perspective%20view&width=700&height=600&seq=analytics-dashboard-001&orientation=landscape"
                alt="Analytics Dashboard"
                className="w-full h-auto rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Stats Banner */}
        <div className="mt-8 sm:mt-12 md:mt-16 grid sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {[
            { value: '10x', label: 'Nhanh hơn trong báo cáo' },
            { value: '100%', label: 'Dữ liệu thời gian thực' },
            { value: '50+', label: 'Mẫu báo cáo có sẵn' },
            { value: '24/7', label: 'Truy cập mọi lúc mọi nơi' }
          ].map((stat, index) => (
            <div key={index} className={`text-center p-4 sm:p-6 rounded-xl bg-gradient-to-br from-[#06b6d4]/10 to-[#00c897]/10 border border-[#06b6d4]/20 transition-all duration-1000 ${
              isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
            }`} style={{ transitionDelay: `${1000 + index * 100}ms` }}>
              <div className="text-3xl sm:text-4xl font-bold text-[#2a4b7c] mb-1 sm:mb-2">{stat.value}</div>
              <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
